import React from 'react';
import { 
  BarChart3, 
  TrendingDown, 
  PieChart, 
  Zap, 
  Layers, 
  Activity, 
  DollarSign, 
  ShieldCheck,
  Search,
  ArrowDownRight,
  TrendingUp,
  Target
} from 'lucide-react';
import './CostOptimization.css';
import Hero from '../../components/Hero/Hero';
import FAQ from '../../components/FAQ/FAQ';
import CTASection from '../../components/CTA/CTA';
import Footer from '../../components/Footer/Footer';

const services = [
  {
    title: 'Rightsizing & Scheduling',
    description: 'We identify underutilized resources and automatically resize or power them down during off-hours, ensuring you only pay for what you use.',
    features: [
      'Automated instance scheduling',
      'CPU/Memory utilization audit',
      'Orphaned resource cleanup'
    ],
    image: '/images/cost-optimization/rightsizing.jpg'
  },
  {
    title: 'Spot & Reserved Instances',
    description: 'Leverage the deepest discounts available from cloud providers by strategically purchasing capacity in advance or using interruptible instances.',
    features: [
      'Savings Plan management',
      'Spot instance fallback design',
      'Multi-year commitment strategy'
    ],
    image: '/images/cost-optimization/spot-instances.jpg'
  },
  {
    title: 'Cost Governance (FinOps)',
    description: 'Establish a culture of cloud accountability with real-time dashboards and automated budget alerts for every department.',
    features: [
      'Tagging & attribution audits',
      'Anomaly detection alerts',
      'Monthly trend forecasting'
    ],
    image: '/images/cost-optimization/governance.jpg'
  }
];

const capabilities = [
  {
    icon: <Search size={24} />,
    title: 'Wastage Audit',
    text: 'A deep scan of your cloud bill to find data transfer costs, unattached storage, and idle compute power.'
  },
  {
    icon: <PieChart size={24} />,
    title: 'Billing Transparency',
    text: 'Break down complex cloud invoices into clear, actionable reports for finance and engineering teams.'
  },
  {
    icon: <TrendingDown size={24} />,
    title: 'Automatic Remediation',
    text: 'Policy-based actions that automatically delete unused staging environments or prune old backups.'
  },
  {
    icon: <Target size={24} />,
    title: 'Unit Cost Analysis',
    text: 'Understand the exact cost of a single customer transaction or user request to improve margins.'
  }
];

const faqItems = [
  {
    question: 'How much can we actually save?',
    answer: 'Most organizations see a 20-35% reduction in their monthly cloud bill within the first three months of implementing FinOps and automated rightsizing.'
  },
  {
    question: 'Will cost optimization impact performance?',
    answer: 'No. Our primary goal is efficiency. We ensure resources match demand perfectly, so performance remains high while costs are minimized during low-traffic periods.'
  },
  {
    question: 'Do you charge a percentage of savings?',
    answer: 'We offer flexible engagement models, including fixed-fee audits or ongoing management, ensuring our incentives are aligned with your long-term efficiency.'
  }
];

export default function CostOptimization() {
  return (
    <article className="cost-page">
      <Hero 
        eyebrow="Cloud Economics"
        title="Maximize Value. Minimize Waste."
        subtitle="Transform your cloud bill from a black box into a source of competitive advantage with expert FinOps and automated optimization."
        stats={[
          { label: 'Avg. Savings', value: '30%' },
          { label: 'ROI', value: 'Instant' }
        ]}
      />

      <section className="section section-capabilities">
        <div className="section-heading">
          <span className="eyebrow">FinOps Excellence</span>
          <h2>Doughnuts do not pay for infrastructure.</h2>
          <p>We provide the tools and strategy to help you reclaim your cloud budget and reinvest it into innovation.</p>
        </div>
        <div className="capabilities-grid">
          {capabilities.map((cap, i) => (
            <div key={i} className="capability-card cost">
              <div className="cap-icon">{cap.icon}</div>
              <h3>{cap.title}</h3>
              <p>{cap.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-solutions">
        <div className="solutions-container">
          {services.map((service, index) => (
            <div key={index} className={`solution-row ${index % 2 !== 0 ? 'reverse' : ''}`}>
              <div className="solution-copy">
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <ul className="feature-list">
                  {service.features.map((f, fi) => (
                    <li key={fi}><Zap size={18} /> {f}</li>
                  ))}
                </ul>
              </div>
              <div className="solution-media">
                <img src={service.image} alt={service.title} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-metrics">
        <div className="metrics-grid">
          <div className="metric-item">
            <strong>30%</strong>
            <span>Avg. Bill Reduction</span>
          </div>
          <div className="metric-item">
            <strong>Real-time</strong>
            <span>Cost Visibility</span>
          </div>
          <div className="metric-item">
            <strong>100%</strong>
            <span>Resource Tagging</span>
          </div>
          <div className="metric-item">
            <strong>Zero</strong>
            <span>Idle Capacity</span>
          </div>
        </div>
      </section>

      <section className="section section-faq">
        <div className="section-heading">
          <span className="eyebrow">Cloud Bills Decoded</span>
          <h2>Value Questions.</h2>
        </div>
        <FAQ items={faqItems} />
      </section>

      <CTASection />
      
      <Footer />
    </article>
  );
}
